import React, { useState, useEffect } from 'react';
import { NavLink, useParams, useNavigate, Link } from 'react-router-dom';
import { Avatar } from 'flowbite-react';
import { navigationGroups, topLevelRoutes, assistantRoute, SiteRoute } from '../../routes/siteRoutes';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { FiMenu, FiX, FiChevronDown, FiChevronRight, FiChevronLeft, FiUser, FiMoon, FiSun } from 'react-icons/fi';
import logo from '../../assets/logo-2024-12-04-no-bg.webp';
import { useUserEmail } from '@/hooks/useUserEmail';
import { useAsync } from 'react-async-hook';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import * as sched from 'sched';

interface SideMenuProps {
  className?: string;
}

const COLLAPSED_W = 'w-14';
const EXPANDED_W = 'w-64';

const SideMenu: React.FC<SideMenuProps> = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('chronomap-sidebar') === 'collapsed');
  const [isMobile, setIsMobile] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['data']));
  const [showUserMenu, setShowUserMenu] = useState(false);

  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { signOut, user } = useAuthenticator((context) => [context.user]);
  const { email } = useUserEmail();
  const { user: authUser, token, client } = useAuth();
  const { toggle: toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();

  const { result: userProfile, loading: profileLoading } = useAsync(async () => {
    if (!authUser || !token || !client) return null;
    try {
      return await sched.WrappedApiClient.get_user_profile(client, token, authUser.userId);
    } catch {
      return null;
    }
  }, [authUser, token, client]);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setIsOpen(false);
        setCollapsed(false);
      } else {
        setIsOpen(true);
      }
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('chronomap-sidebar', next ? 'collapsed' : 'expanded');
  };

  const toggleGroup = (groupId: string) => {
    if (collapsed) return;
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const collapseAfterNav = () => {
    if (isMobile) {
      setIsOpen(false);
    } else {
      setCollapsed(true);
      localStorage.setItem('chronomap-sidebar', 'collapsed');
    }
  };

  // Common nav item classes — consistent for ALL items
  const navCls = (isActive: boolean, isCollapsed: boolean) =>
    `flex items-center ${isCollapsed ? 'justify-center' : ''} p-2 text-sm font-medium rounded-lg transition-colors ${
      isActive
        ? 'bg-cm-bg-elevated text-cm-accent-orange'
        : 'text-cm-text-secondary hover:bg-cm-bg-hover hover:text-cm-text-primary'
    }`;

  const subNavCls = (isActive: boolean) =>
    `flex items-center p-2 text-sm font-medium rounded-lg transition-colors ${
      isActive
        ? 'bg-cm-bg-elevated text-cm-accent-orange'
        : 'text-cm-text-muted hover:bg-cm-bg-hover hover:text-cm-text-secondary'
    }`;

  return (
    <div className={`relative ${className}`}>
      {/* Mobile hamburger */}
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-secondary"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <FiX className="h-5 w-5" /> : <FiMenu className="h-5 w-5" />}
      </button>

      {/* Sidebar panel — always floats over content */}
      <div
        className={`
          fixed top-0 left-0 z-40 h-screen transition-all duration-200 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${collapsed ? COLLAPSED_W : EXPANDED_W}
        `}
      >
        <div className={`h-screen py-4 ${collapsed ? 'px-1.5' : 'px-3'} bg-cm-bg-surface border-r border-cm-border-subtle flex flex-col`}>
          {/* Logo + collapse toggle */}
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'} mb-5`}>
            {collapsed ? (
              <button
                onClick={toggleCollapse}
                className="p-1.5 rounded text-cm-text-muted hover:text-cm-accent-orange hover:bg-cm-bg-hover transition-colors"
                title="Expand sidebar"
              >
                <FiMenu className="w-5 h-5" />
              </button>
            ) : (
              <>
                <Link to="/workspaces" className="flex items-center min-w-0">
                  <img src={logo} alt="ChronoMap logo" className="h-8 w-8 shrink-0" />
                  <span className="text-xl font-mono font-semibold text-cm-accent-orange ml-3 truncate">chronomap</span>
                </Link>
                {!isMobile && (
                  <button
                    onClick={toggleCollapse}
                    className="p-1 rounded text-cm-text-muted hover:text-cm-text-primary hover:bg-cm-bg-hover transition-colors shrink-0"
                    title="Collapse sidebar"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                  </button>
                )}
              </>
            )}
          </div>

          {/* Navigation Links */}
          <div className="flex-1 space-y-1 overflow-y-auto overflow-x-hidden">
            {topLevelRoutes.map((route: SiteRoute) => {
              const Icon = route.icon;
              return (
                <NavLink
                  key={route.path}
                  to={`/workspaces/${workspaceId}/${route.path}`}
                  onClick={collapseAfterNav}
                  title={collapsed ? route.label : undefined}
                  className={({ isActive }) => navCls(isActive, collapsed)}
                >
                  {Icon && <Icon className={`w-5 h-5 ${collapsed ? '' : 'mr-3'} shrink-0`} />}
                  {!collapsed && <span className="truncate">{route.label}</span>}
                </NavLink>
              );
            })}

            <div className="border-t border-cm-border-subtle my-2" />

            {navigationGroups.map((group) => {
              const GroupIcon = group.icon;
              const isExpanded = expandedGroups.has(group.id);

              return (
                <div key={group.id} className="space-y-1">
                  <button
                    onClick={() => {
                      if (collapsed) {
                        setCollapsed(false);
                        localStorage.setItem('chronomap-sidebar', 'expanded');
                        setExpandedGroups(prev => new Set(prev).add(group.id));
                      } else {
                        toggleGroup(group.id);
                      }
                    }}
                    title={collapsed ? group.label : undefined}
                    className={`w-full ${navCls(false, collapsed)}`}
                  >
                    <GroupIcon className={`w-5 h-5 ${collapsed ? '' : 'mr-3'} shrink-0`} />
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-left truncate">{group.label}</span>
                        {isExpanded ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
                      </>
                    )}
                  </button>

                  {!collapsed && isExpanded && (
                    <div className="ml-6 space-y-1">
                      {group.routes.map((route: SiteRoute) => {
                        const Icon = route.icon;
                        return (
                          <NavLink
                            key={route.path}
                            to={`/workspaces/${workspaceId}/${route.path}`}
                            onClick={collapseAfterNav}
                            className={({ isActive }) => subNavCls(isActive)}
                          >
                            {Icon && <Icon className="w-4 h-4 mr-3 shrink-0" />}
                            <span className="truncate">{route.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Assistant (just above user section) */}
          <div className="pt-2 flex-none">
            {(() => {
              const Icon = assistantRoute.icon;
              return (
                <NavLink
                  to={`/workspaces/${workspaceId}/${assistantRoute.path}`}
                  onClick={collapseAfterNav}
                  title={collapsed ? assistantRoute.label : undefined}
                  className={({ isActive }) => navCls(isActive, collapsed)}
                >
                  {Icon && <Icon className={`w-5 h-5 ${collapsed ? '' : 'mr-3'} shrink-0`} />}
                  {!collapsed && <span className="truncate">{assistantRoute.label}</span>}
                </NavLink>
              );
            })()}
          </div>

          {/* User section */}
          <div className="border-t border-cm-border-subtle pt-3 mt-3 relative flex-none">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              title={collapsed ? (userProfile?.display_name || email || 'User') : undefined}
              className={`w-full flex items-center ${collapsed ? 'justify-center' : ''} p-2 text-sm font-medium text-cm-text-secondary hover:bg-cm-bg-hover hover:text-cm-text-primary rounded-lg transition-colors`}
            >
              <Avatar
                alt="User profile"
                img={userProfile?.avatar_url || undefined}
                rounded
                size="sm"
                className={collapsed ? '' : 'mr-3'}
              />
              {!collapsed && (
                <div className="flex-1 text-left min-w-0">
                  {profileLoading ? (
                    <div className="animate-pulse">
                      <div className="h-4 bg-cm-bg-elevated rounded w-24 mb-1"></div>
                      <div className="h-3 bg-cm-bg-hover rounded w-32"></div>
                    </div>
                  ) : userProfile ? (
                    <div>
                      <div className="font-medium text-cm-text-primary truncate">{userProfile.display_name}</div>
                      <div className="text-xs text-cm-text-muted truncate">{userProfile.email}</div>
                    </div>
                  ) : (
                    <span className="truncate">{email || user?.username || 'User'}</span>
                  )}
                </div>
              )}
            </button>

            {showUserMenu && (
              <div className={`absolute bottom-full left-0 mb-2 bg-cm-bg-elevated rounded-lg shadow-xl border border-cm-border-mid py-2 z-50 ${collapsed ? 'w-48' : 'right-0'}`}>
                <ul>
                  <li>
                    <button
                      className="flex items-center w-full px-4 py-2 text-sm text-cm-text-secondary hover:bg-cm-bg-hover hover:text-cm-text-primary text-left transition-colors"
                      onClick={() => {
                        navigate(`/workspaces/${workspaceId}/profile`);
                        setShowUserMenu(false);
                        isMobile && setIsOpen(false);
                      }}
                    >
                      <FiUser className="w-4 h-4 mr-3" />
                      Profile
                    </button>
                  </li>
                  <li>
                    <button
                      className="flex items-center w-full px-4 py-2 text-sm text-cm-text-secondary hover:bg-cm-bg-hover hover:text-cm-text-primary text-left transition-colors"
                      onClick={() => { toggleTheme(); }}
                    >
                      {isDark ? <FiSun className="w-4 h-4 mr-3" /> : <FiMoon className="w-4 h-4 mr-3" />}
                      {isDark ? 'Light mode' : 'Dark mode'}
                    </button>
                  </li>
                  <li className="border-t border-cm-border-subtle mt-1 pt-1">
                    <button
                      className="flex items-center w-full px-4 py-2 text-sm text-cm-accent-orange hover:bg-cm-bg-hover"
                      onClick={signOut}
                    >
                      Sign out
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Spacer — always collapsed width so sidebar floats over content when expanded */}
      <div className={`hidden md:block ${COLLAPSED_W} shrink-0`} aria-hidden />

      {/* Overlay when expanded (to allow click-to-collapse) */}
      {!isMobile && !collapsed && (
        <div className="fixed inset-0 z-30" onClick={toggleCollapse} />
      )}

      {/* Mobile overlay */}
      {isMobile && isOpen && (
        <div className="fixed inset-0 bg-cm-bg-deep/50 z-30" onClick={() => setIsOpen(false)} />
      )}

      {/* Close user menu overlay */}
      {showUserMenu && (
        <div className="fixed inset-0 z-35" onClick={() => setShowUserMenu(false)} />
      )}
    </div>
  );
};

export default SideMenu;
